// Parses the "Member Preferences" Google-Form CSV export into app players.
//
// That form has a very different shape from the app's own JSON: one column
// per weekday holding the 1.5-hour time WINDOWS a member selected, a matching
// "ranked top 3" column, a skill rating, preferred partners, free-text notes,
// and a private-lesson block. This module maps every one of those fields onto
// the current player schema so nothing the member sent is lost:
//   • rating            → skillLevel ("Advanced (4.5–5.0)")
//   • per-day windows    → availability, expanded to the app's HOURLY slots
//   • private windows    → privateTimes
//   • preferred partners → preferredPartners
//   • notes + ranked prefs (+ any age found) → notes / age / classification
//
// The result matches parseUploadedPlayers()'s shape so RosterView can treat
// both upload types identically.

import { parseTime, getOccupiedSlotsForRange } from "./time";
import { DAYS } from "./tiers";
import { normalizeUploadedPlayer } from "./importPlayers";

// Repairs the "UTF-8 decoded as Latin-1" mojibake that can appear when the
// form export is re-saved, and normalises whitespace. A clean UTF-8 file is
// left untouched.
function clean(s) {
  if (typeof s !== "string") return "";
  return s
    .replace(/â€“/g, "–") // en dash
    .replace(/â€”/g, "—") // em dash
    .replace(/â€™/g, "’") // apostrophe
    .replace(/â€/g, "”")
    .replace(/â€/g, "“")
    .replace(/ /g, " ")
    .trim();
}

// A minimal RFC-4180 CSV tokenizer: handles quoted fields, escaped ""
// quotes, and commas/newlines inside quotes.
function parseCSVRows(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\r") {
      // ignore — handled with the \n
    } else if (c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// "D. *Advanced* (4.5–5.0)" -> "Advanced (4.5–5.0)"
function parseSkillLevel(raw) {
  return clean(raw).replace(/^[A-Za-z]\.\s*/, "").replace(/\*/g, "").trim();
}

// Expands one selected window ("10:30 AM–12:00 PM") into the whole-hour
// TIME_SLOTS labels it overlaps — the form of availability the scheduler's
// warnings actually compare against.
function windowToSlots(win) {
  const parts = clean(win).split(/\s*[–—-]\s*/);
  if (parts.length < 2) return [];
  const startStr = parts[0].replace(/\s+/g, " ").trim();
  const endStr = parts[1].replace(/\s+/g, " ").trim();
  const start = parseTime(startStr);
  const end = parseTime(endStr);
  if (start == null || end == null || end <= start) return [];
  return getOccupiedSlotsForRange(startStr, (end - start) / 60);
}

// The form has no age/classification column, but parents routinely name the
// child's age in the free text ("my son who is 7", "a child (7)"). Pull it out
// so juniors land as juniors; stay conservative to avoid mis-reading adults.
function detectJunior(text) {
  const t = (text || "").toLowerCase();
  const hasChildContext =
    /(child|kid|son|daughter|\bboy\b|\bgirl\b)/.test(t) ||
    /\bage[d]?\b/.test(t) ||
    /\byears?\s*old\b|\byo\b/.test(t);
  if (!hasChildContext) return { age: null, classification: "Adult" };
  // First standalone 1–2 digit number that isn't a decimal (1.5hr), a clock
  // time (4PM), or a duration (2 hr).
  const m = t.match(/\b(\d{1,2})\b(?!\s*(?:\.|:|\d|hr|hour|hrs|h\b|min|am|pm))/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 2 && n <= 17) return { age: n, classification: "Junior" };
  }
  return { age: null, classification: "Adult" };
}

function isBlank(v) {
  return !v || /^(n\/?a|none|no)$/i.test(v.trim());
}

export function parseMemberPreferencesCSV(csvText, existingIds) {
  const rows = parseCSVRows(csvText).filter(r => r.some(c => clean(c) !== ""));
  if (rows.length < 2) {
    return { error: "That CSV has no member rows to import." };
  }

  // Locate columns by header text so the parser survives column reordering.
  const header = rows[0].map(h => clean(h).toLowerCase());
  const find = pred => header.findIndex(pred);
  const col = {
    first: find(h => h.includes("first name")),
    last: find(h => h.includes("last name")),
    email: find(h => h.includes("email")),
    rating: find(h => h.includes("rating") || h.includes("level")),
    partners: find(h => h.includes("other members") || h.includes("playing with")),
    notes: find(h => h.includes("anything else")),
  };
  // Each weekday's ranked column names the day; its window-options column is
  // the "Time window options" header immediately before it.
  const dayCols = {};
  DAYS.forEach(day => {
    const rank = find(h => h.includes(day.toLowerCase()) && h.includes("rank"));
    if (rank > 0) dayCols[day] = { windows: rank - 1, rank };
  });
  const privRank = find(h => h.includes("private") && h.includes("rank"));
  const privWindows = privRank > 0 ? privRank - 1 : -1;

  if (col.first < 0 && col.email < 0) {
    return { error: "This doesn't look like the Member Preferences form — no name/email columns found." };
  }

  const idSet = new Set(existingIds);
  const players = [];
  let skipped = 0;

  rows.slice(1).forEach(cells => {
    const get = i => (i >= 0 && i < cells.length ? clean(cells[i]) : "");

    const name = `${get(col.first)} ${get(col.last)}`.trim();
    if (!name) { skipped++; return; }

    // Availability: union of every selected window across the weekdays,
    // expanded to the app's hourly slots and de-duplicated.
    const availSet = new Set();
    DAYS.forEach(day => {
      const c = dayCols[day];
      if (!c) return;
      const windowsText = get(c.windows);
      if (!windowsText) return;
      windowsText.split(",").forEach(w => {
        windowToSlots(w).forEach(slot => availSet.add(`${day}|${slot}`));
      });
    });

    // Private lessons: keep the selected windows, unless the member opted out.
    let privateTimes = "";
    if (privWindows >= 0) {
      const pw = get(privWindows);
      if (pw && !/don.?t\s*want|no private|not interested/i.test(pw) && !isBlank(pw)) {
        privateTimes = pw;
      }
    }

    const partners = get(col.partners);
    const baseNotes = get(col.notes);

    // Preserve ranked time preferences verbatim (the letters reference the
    // form's own legend) so no submitted data is silently dropped.
    const noteParts = [];
    if (!isBlank(baseNotes)) noteParts.push(baseNotes);
    const rankBits = [];
    DAYS.forEach(day => {
      const c = dayCols[day];
      if (!c) return;
      const r = get(c.rank);
      if (!isBlank(r)) rankBits.push(`${day.slice(0, 3)}: ${r}`);
    });
    if (rankBits.length) noteParts.push(`Ranked time prefs — ${rankBits.join("; ")}`);
    const privR = privRank >= 0 ? get(privRank) : "";
    if (!isBlank(privR)) noteParts.push(`Private prefs: ${privR}`);

    const { age, classification } = detectJunior(`${partners} ${baseNotes}`);

    const raw = {
      name,
      lastName: get(col.last),
      email: get(col.email),
      classification,
      age,
      availability: [...availSet],
      skillLevel: parseSkillLevel(get(col.rating)),
      preferredPartners: isBlank(partners) ? "" : partners,
      privateTimes,
      notes: noteParts.join(" | "),
    };

    const player = normalizeUploadedPlayer(raw, idSet);
    if (player) players.push(player);
    else skipped++;
  });

  return { players, skipped, total: rows.length - 1 };
}
